import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import projectPlanDraftService from '@services/projectPlanDraft';
import {
  CreateProjectPlanDraftInput,
  ImportProjectPlanDraftInput,
  ProjectPlanDraft,
  ReviseProjectPlanDraftInput,
  SaveProjectPlanDraftAsTemplateInput,
  UpdateProjectPlanDraftPlanInput,
} from '@models/projectPlanDraft.model';
import { getErrorMessage } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';
import { PROJECT_PLAN_DRAFT_QUERY_KEY } from './useProjectPlanDraft';
import { MY_PROJECT_PLAN_DRAFTS_QUERY_KEY } from './useMyProjectPlanDrafts';
import { PROJECTS_QUERY_KEY } from '@hooks/api/projects/useProjects';
import { PROJECTS_BY_STATE_QUERY_KEY } from '@hooks/api/projects/useProjectsByState';
import { TASK_TEMPLATES_QUERY_KEY } from '@hooks/api/taskTemplates';

/**
 * Sprint 31, story-161. All seven `ProjectPlanDraft` mutations, one hook each — mirroring
 * `taskTemplate`'s hook-per-mutation convention. None of these show a generic "success" toast on
 * `create`/`revise`/`retry`/`updatePlan`: the review screen's own UI (the animated tree, the
 * chat reply) already communicates the result, and the polling hook drives phase transitions —
 * a toast on every 2s-polled `GENERATING` -> `READY` flip would be noise.
 */

const seedDraftCache = (queryClient: ReturnType<typeof useQueryClient>, draft: ProjectPlanDraft) => {
  queryClient.setQueryData([PROJECT_PLAN_DRAFT_QUERY_KEY, draft.draftId], draft);
};

export const useCreateProjectPlanDraft = () => {
  const queryClient = useQueryClient();
  const tActionMessages = useTranslation('actionMessages');
  return useMutation({
    mutationKey: ['createProjectPlanDraft'],
    mutationFn: (input: CreateProjectPlanDraftInput) => projectPlanDraftService.createProjectPlanDraft(input),
    onSuccess: async (draft) => {
      seedDraftCache(queryClient, draft);
      await queryClient.invalidateQueries({ queryKey: [MY_PROJECT_PLAN_DRAFTS_QUERY_KEY] });
    },
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useReviseProjectPlanDraft = () => {
  const queryClient = useQueryClient();
  const tActionMessages = useTranslation('actionMessages');
  return useMutation({
    mutationKey: ['reviseProjectPlanDraft'],
    mutationFn: (input: ReviseProjectPlanDraftInput) => projectPlanDraftService.reviseProjectPlanDraft(input),
    onSuccess: (draft) => seedDraftCache(queryClient, draft),
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useRetryProjectPlanDraft = () => {
  const queryClient = useQueryClient();
  const tActionMessages = useTranslation('actionMessages');
  return useMutation({
    mutationKey: ['retryProjectPlanDraft'],
    mutationFn: (draftId: string) => projectPlanDraftService.retryProjectPlanDraft(draftId),
    onSuccess: (draft) => seedDraftCache(queryClient, draft),
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useUpdateProjectPlanDraftPlan = () => {
  const queryClient = useQueryClient();
  const tActionMessages = useTranslation('actionMessages');
  return useMutation({
    mutationKey: ['updateProjectPlanDraftPlan'],
    mutationFn: (input: UpdateProjectPlanDraftPlanInput) => projectPlanDraftService.updateProjectPlanDraftPlan(input),
    onSuccess: (draft) => seedDraftCache(queryClient, draft),
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useImportProjectPlanDraft = () => {
  const queryClient = useQueryClient();
  const tActionMessages = useTranslation('actionMessages');
  return useMutation({
    mutationKey: ['importProjectPlanDraft'],
    mutationFn: (input: ImportProjectPlanDraftInput) => projectPlanDraftService.importProjectPlanDraft(input),
    onSuccess: async () => {
      toast.success(tActionMessages.planImportedSuccessfully);
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: [MY_PROJECT_PLAN_DRAFTS_QUERY_KEY] });
    },
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useSaveProjectPlanDraftAsTemplate = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['saveProjectPlanDraftAsTemplate'],
    mutationFn: (input: SaveProjectPlanDraftAsTemplateInput) =>
      projectPlanDraftService.saveProjectPlanDraftAsTemplate(input),
    onSuccess: async () => {
      toast.success(tActionMessages.planSavedAsTemplate);
      await queryClient.invalidateQueries({ queryKey: [TASK_TEMPLATES_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [MY_PROJECT_PLAN_DRAFTS_QUERY_KEY] });
    },
    onError: (error: any) => toast.error(getErrorMessage(error, tActionMessages.planGenerationFailed)),
  });
};

export const useDiscardProjectPlanDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['discardProjectPlanDraft'],
    mutationFn: (draftId: string) => projectPlanDraftService.discardProjectPlanDraft(draftId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MY_PROJECT_PLAN_DRAFTS_QUERY_KEY] }),
  });
};

/** Re-exported for the wizard's post-commit invalidation, matching `useCreateProject`'s own list. */
export const PROJECT_PLAN_DRAFT_RELATED_QUERY_KEYS = [PROJECTS_QUERY_KEY, PROJECTS_BY_STATE_QUERY_KEY];
